import type { PerformancePart, StageInputStand } from '@tunasong/schemas'

/**
 * Preset tiers for the stage setup editor's "Start from a preset" step.
 *
 * NEEDS A FOH REVIEW BEFORE LAUNCH. These are production conventions, not rules: which mic type goes on which source,
 * the stand, and when 48V is on (condensers here; active DIs are left off, since a passive DI needs none). A working
 * front-of-house engineer should check every row, together with the suggestion lists in `./suggestions.ts`. The tier
 * labels are the spec's wording (docs/ux/2026-10-16-rider-collaboration-ux.md, "Presets are tiers, not one list").
 *
 * Plain data on purpose: reviewable without reading UI code. A tier is a starting point, not a mode; nothing
 * remembers which tier a member picked, and every added row stays editable.
 */

export interface PresetRow {
  source: string
  micDi?: string
  stand?: StageInputStand
  phantomPower?: boolean
}

export interface PresetTier {
  id: string
  label: string
  rows: PresetRow[]
}

const kick: PresetRow = { source: 'Kick', micDi: 'Kick mic', stand: 'short-boom' }
const snare: PresetRow = { source: 'Snare', micDi: 'Dynamic', stand: 'short-boom' }
const hiHat: PresetRow = { source: 'Hi-hat', micDi: 'Condenser', stand: 'short-boom', phantomPower: true }
const tom = (n: number): PresetRow => ({ source: `Tom ${n}`, micDi: 'Dynamic', stand: 'clip' })
const overheadL: PresetRow = { source: 'OH L', micDi: 'Condenser', stand: 'tall-boom', phantomPower: true }
const overheadR: PresetRow = { source: 'OH R', micDi: 'Condenser', stand: 'tall-boom', phantomPower: true }
const di = (source: string): PresetRow => ({ source, micDi: 'DI', stand: 'none' })

export const presetTiers: Partial<Record<PerformancePart, PresetTier[]>> = {
  vocals: [
    { id: 'one', label: '1 vocal mic', rows: [{ source: 'Vocal', micDi: 'Dynamic', stand: 'tall-boom' }] },
    {
      id: 'two',
      label: 'Two vocal mics (second position or effect mic)',
      rows: [
        { source: 'Vocal', micDi: 'Dynamic', stand: 'tall-boom' },
        { source: 'Vocal 2', micDi: 'Dynamic', stand: 'tall-boom' },
      ],
    },
  ],
  guitar: [
    { id: 'amp', label: 'Amp mic', rows: [{ source: 'Guitar amp', micDi: 'Dynamic', stand: 'short-boom' }] },
    { id: 'di', label: 'DI', rows: [di('Guitar DI')] },
    {
      id: 'amp-di',
      label: 'Amp mic + DI',
      rows: [{ source: 'Guitar amp', micDi: 'Dynamic', stand: 'short-boom' }, di('Guitar DI')],
    },
  ],
  bass: [
    { id: 'amp', label: 'Amp mic', rows: [{ source: 'Bass amp', micDi: 'Kick mic', stand: 'short-boom' }] },
    { id: 'di', label: 'DI', rows: [di('Bass DI')] },
    {
      id: 'amp-di',
      label: 'Amp mic + DI',
      rows: [{ source: 'Bass amp', micDi: 'Kick mic', stand: 'short-boom' }, di('Bass DI')],
    },
  ],
  keys: [
    { id: 'mono', label: 'Mono DI', rows: [di('Keys')] },
    { id: 'stereo', label: 'Stereo DI (L/R)', rows: [di('Keys L'), di('Keys R')] },
    {
      id: 'two-stereo',
      label: '2 keyboards, stereo (4 DI)',
      rows: [di('Keys 1 L'), di('Keys 1 R'), di('Keys 2 L'), di('Keys 2 R')],
    },
  ],
  drums: [
    { id: 'minimal', label: 'Minimal: kick + 2 overheads', rows: [kick, overheadL, overheadR] },
    {
      id: 'standard',
      label: 'Standard: kick, snare, hi-hat, 2 toms, 2 overheads',
      rows: [kick, snare, hiHat, tom(1), tom(2), overheadL, overheadR],
    },
    {
      id: 'full',
      label: 'Full: standard + kick in/out, snare bottom, 3rd tom',
      rows: [
        { source: 'Kick in', micDi: 'Kick mic', stand: 'none' },
        { source: 'Kick out', micDi: 'Kick mic', stand: 'short-boom' },
        { source: 'Snare top', micDi: 'Dynamic', stand: 'short-boom' },
        { source: 'Snare bottom', micDi: 'Dynamic', stand: 'clip' },
        hiHat,
        tom(1),
        tom(2),
        tom(3),
        overheadL,
        overheadR,
      ],
    },
  ],
}
